import { API } from "@swivl-mono/api-helper";
import { IsTruthy } from "@swivl-mono/helpers";
import { LocationVarKey } from "@swivl-mono/types";
import { Button, ButtonColor, ButtonSize, ButtonVariant } from "@swivl-mono/ui";
import { useState } from "react";
import { FaTimes } from "react-icons/fa";

import { BroadcastModal } from "../../../../../Components/Conversations/BroadcastModal/BroadcastModal";
import LocationsModel from "../../../../../Models/Locations/Locations.Model";
import { Member } from "../../../../../Models/Member/Member.Current";
import ModalModel from "../../../../../Models/Modal/Modal.Model";
import LocationVarsModel from "../../../../../Models/Variables/LocationVars.Model";

interface SMSCampaignButtonProps {
  preselectedLocationIds?: number[];
  preselectedTenantIds?: number[];
  size?: ButtonSize;
  color?: ButtonColor;
  /**
   * Which org var gates the button. The legacy v2 surface reads
   * `HasSMSCampaigns` (default); Snowy surfaces (CC v3) pass
   * `LocationVarKey.CortexIsSMSCampaignsEnabled` — Cortex-only reads
   * (ENG-2946).
   */
  gateVarKey?: LocationVarKey;
}

export function SMSCampaignButton({
  preselectedLocationIds = [],
  preselectedTenantIds = [],
  size = ButtonSize.SMALL,
  color = ButtonColor.ORANGE,
  gateVarKey = LocationVarKey.HasSMSCampaigns
}: SMSCampaignButtonProps) {
  const vars = LocationVarsModel.useState((s) => s);
  const { locationsWithVariables } = LocationsModel.useState((s) => s);
  const [isLoadingLocations, setIsLoadingLocations] = useState(false);
  const hasSMSCampaigns = IsTruthy(vars?.orgVariables?.[gateVarKey]?.value);

  const handleOpenModal = async () => {
    let locationIds = preselectedLocationIds;

    // if tenantids, fetch their locations and filter to sms-enabled ones
    if (preselectedTenantIds.length > 0) {
      setIsLoadingLocations(true);
      try {
        // fetch tenants for each selected user
        const tenantPromises = preselectedTenantIds.map((userId) => API.Tenants.getTenantsByUserId(Member.orgId!, userId));
        const tenantArrays = await Promise.all(tenantPromises);
        const allTenants = tenantArrays.flat();

        // extract unique location ids from tenants
        const tenantLocationIds = Array.from(new Set(allTenants.map((tenant) => tenant.locationId)));

        // filter to only sms-enabled locations
        if (locationsWithVariables && Object.keys(locationsWithVariables).length > 0) {
          // `variables` values are plain strings here (LocationWithVariablesInterface),
          // not {value} objects — the previous `.value` read was always undefined,
          // which silently emptied this filter.
          const smsEnabledLocationIds = Object.values(locationsWithVariables)
            .filter(({ variables }) => IsTruthy(variables[gateVarKey]))
            .map(({ location }) => location.id);

          locationIds = tenantLocationIds.filter((id) => smsEnabledLocationIds.includes(id));
        } else {
          locationIds = tenantLocationIds;
        }
      } catch (error) {
        console.error("failed to fetch tenant locations:", error);
      } finally {
        setIsLoadingLocations(false);
      }
    }

    ModalModel.showModalWithContent(
      <div className="flex w-full items-center justify-between">
        <h2 className="text-2xl text-grey-800">New SMS Campaign</h2>
        <Button
          variant={ButtonVariant.GHOST}
          onClick={() => {
            ModalModel.hideModal();
          }}
        >
          <FaTimes />
        </Button>
      </div>,
      <BroadcastModal preselectedLocationIds={locationIds} preselectedTenantIds={preselectedTenantIds} />,
      544,
      600,
      false
    );
  };

  return (
    <>
      {hasSMSCampaigns && (
        <Button color={color} size={size} onClick={handleOpenModal} loading={isLoadingLocations}>
          New SMS
        </Button>
      )}
    </>
  );
}
